import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DocumentService } from "@app/services/document.service";
import {
  IDocument,
  IDocumentPageCalculatedValue,
  IDocumentPages,
  IGenerativeBusinessDocumentDTO,
} from "@app/commons/types/interfaces/documents.interface";
import { ViewConfig } from "@app/commons/utils/view-config";
import {
  formatBytes,
  getBooleanToYesNo,
  getErrorMessage,
  isDocumentTypePdf,
  isDocumentTypeTiff,
  isDocumentTypeJpg,
  isDocumentTypePng,
} from "@app/commons/utils/utils";
import {
  FileConstants,
  GenerativeDocumentSearchDataStorage,
} from "@app/commons/types/enums/constants.enums";
import { LocalStorageCacheService } from "@app/services/local-storage-cache.service";
import { SnackbarService } from "@app/services/snackbar.service";
import { DOCUMENT_DETAIL_KEY_LABEL_MAPPING } from "../../constants";
import { DataService } from "@app/services/data.service";

declare var Tiff: any;

@Component({
  selector: "app-document-detail",
  templateUrl: "./document-detail.component.html",
  styleUrl: "./document-detail.component.scss",
  //encapsulation: ViewEncapsulation.None,
})
export class DocumentDetailComponent implements OnInit {
  viewConfig = new ViewConfig();
  documentFileViewConfig = new ViewConfig();
  @ViewChild("canvas", { static: true }) canvas!: ElementRef<HTMLCanvasElement>;

  documentId: any;
  documentDetails: IDocument = {};

  backupDocumentDetails: IDocumentPages[] = [];
  showShowAllButton: boolean = false;

  fileUrl: string | undefined;
  page = 1; // default page of pdf

  getBooleanToYesNo = getBooleanToYesNo;
  formatBytes = formatBytes;
  isDocumentTypePdf = isDocumentTypePdf;
  isDocumentTypeTiff = isDocumentTypeTiff;
  isDocumentTypeJpg = isDocumentTypeJpg;
  isDocumentTypePng = isDocumentTypePng;

  constructor(
    private route: ActivatedRoute,
    private documentService: DocumentService,
    private localStorageCacheService: LocalStorageCacheService,
    private dataService: DataService,
    private snackBarService: SnackbarService,
    private router: Router
  ) {}

  onPdfLoad() {
    this.documentFileViewConfig.setLoading(false);
  }

  onPdfExpand() {
    if (this.documentFileViewConfig.loading) {
      this.snackBarService.show("Please wait while the document loads.");
      return;
    }
    // for image just open the file in new tab
    if (isDocumentTypePdf(this.documentDetails.fileType)) {
      // for pdf, open the pdf viewer url
      this.router.navigate(["/documents", this.documentId, "full-screen"], {
        queryParams: {
          filename: this.documentDetails?.name,
        },
      });
      return;
    } else {
      window.open(this.fileUrl, "_blank");
    }
  }

  onBack() {
    this.router.navigate(["/documents"]);
  }

  ngOnInit(): void {
    this.documentId = this.route.snapshot.paramMap.get("documentId");
    this.getDocumentDetail();
    window.scroll({ top: 0 });
  }

  showGenAIResults() {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedPageFromURL = urlParams.get("pageNumber");
    const selectedPage = parseInt(selectedPageFromURL ?? "0");
    if (selectedPage) {
      if (selectedPage > 0) {
        this.navigateToPdfPage(selectedPage);
      }

      const availablePages = [selectedPage];
      this.documentDetails.pages = this.backupDocumentDetails?.filter((x) =>
        x.pageNumber ? availablePages?.includes(x.pageNumber) : false
      );

      this.showShowAllButton = true;
    } else {
      this.documentDetails.pages = this.backupDocumentDetails;
    }
  }

  reset() {
    this.showShowAllButton = false;
    this.documentDetails.pages = this.backupDocumentDetails;
    console.log(this.backupDocumentDetails?.length);
    this.previewData();
  }

  populateDocumentDetails(documentDetails: IDocument): void {
    documentDetails.pages?.forEach((page: IDocumentPages) => {
      const value: IDocumentPageCalculatedValue[] = [];
      Object.keys(DOCUMENT_DETAIL_KEY_LABEL_MAPPING).forEach((key) => {
        const v = (page as any)[key];
        if (v) {
          value.push({
            key: (DOCUMENT_DETAIL_KEY_LABEL_MAPPING as any)[key],
            value: this.isDate(v) ? this.formatDate(v) : v,
          });
        }
      });
      page.calculatedDetails = value;
    });
  }

  getDocumentDetail() {
    this.viewConfig.setLoading(true);
    this.documentService.getDocumentDetail(this.documentId).subscribe({
      next: (result: IDocument) => {
        this.viewConfig.setLoading(false);
        this.backupDocumentDetails = result.pages ?? [];
        this.documentDetails = result;
        this.showGenAIResults();
        this.previewData();
        if (this.fileExists()) {
          this.getDocumentFile();
        }
      },
      error: (e) => {
        this.viewConfig.setError(getErrorMessage(e));
      },
    });
  }

  previewData() {
    this.documentDetails.pages = this.documentDetails.pages?.sort((a, b) => {
      if (a?.pageNumber && b?.pageNumber) {
        return a.pageNumber - b?.pageNumber;
      }
      return 0;
    });
    this.populateDocumentDetails(this.documentDetails);
    this.localStorageCacheService.set(
      "document",
      this.documentDetails,
      FileConstants.DocumentLocalStorageExpiresInMinutes
    );
  }

  getDocumentFile() {
    this.documentFileViewConfig.setLoading(true);

    // load from cache
    const existingUrl = this.localStorageCacheService.get(this.documentId);
    if (existingUrl) {
      this.fileUrl = existingUrl;
      if (
        isDocumentTypeJpg(this.documentDetails.fileType) ||
        isDocumentTypePng(this.documentDetails.fileType)
      ) {
        this.documentFileViewConfig.setLoading(false);
      }
      if (isDocumentTypeTiff(this.documentDetails.fileType)) {
        this.loadTiffFromUrl(this.fileUrl);
      }
      return;
    }

    this.documentService
      .getDocumentFile(
        this.documentDetails.bucketName,
        this.documentDetails?.name,
        FileConstants.DownloadExpiresIn.toString()
      )
      .subscribe({
        next: (result: any) => {
          // this.documentFileViewConfig.setLoading(false);
          // commented this as pdf file is still loading in viewer, we will set it false in onPdfLoad function
          this.fileUrl = result.url;
          this.localStorageCacheService.set(
            this.documentId,
            this.fileUrl,
            FileConstants.DocumentFileLocalStorageExpiresInMinutes
          );
          if (
            isDocumentTypeJpg(this.documentDetails.fileType) ||
            isDocumentTypePng(this.documentDetails.fileType)
          ) {
            this.documentFileViewConfig.setLoading(false);
          }
          if (isDocumentTypeTiff(this.documentDetails.fileType)) {
            this.loadTiffFromUrl(this.fileUrl);
          }
        },
        error: (e) => {
          this.documentFileViewConfig.setError(getErrorMessage(e));
        },
      });
  }

  navigateToPdfPage(pageNumber: number = 0) {
    this.page = pageNumber;
  }

  fileExists() {
    return this.documentDetails?.name;
  }

  async loadTiffFromUrl(signedUrl: string | undefined) {
    try {
      if (!signedUrl) {
        this.documentFileViewConfig.setError(
          "The url is not loaded, please try again."
        );
        return;
      }
      // Fetch the TIFF file as an ArrayBuffer
      const response = await fetch(signedUrl);
      if (!response.ok) throw new Error("Failed to load TIFF file");

      const arrayBuffer = await response.arrayBuffer();
      const tiff = new Tiff({ buffer: arrayBuffer });

      // Get the width and height of the image
      const width = tiff.width();
      const height = tiff.height();

      // Get the canvas and context
      const canvas = this.canvas.nativeElement;

      // Set a desired size for the canvas
      const scaleFactor = 0.25; // Scale down by 50% (adjust as needed)
      const canvasWidth = width * scaleFactor;
      const canvasHeight = height * scaleFactor;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        ctx.drawImage(tiff.toCanvas(), 0, 0, canvasWidth, canvasHeight);
        this.documentFileViewConfig.setLoading(false);
      }
    } catch (error) {
      console.error("Error loading TIFF:", error);
      this.documentFileViewConfig.setError(getErrorMessage(error));
    }
  }

  isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  formatDate(date: string): string {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  }
}
